import { ref, computed, watchEffect, watch } from 'vue';
import { defineStore } from 'pinia';
import { getRepo, getRepoWithAudit, getAllWl } from '@/api/repo';
import getRepoId from '@/utils/getRepoId';
import { setting } from '@/setting';
import { getSourceInfo } from '@/api/common';
import { reqCatch } from '@/utils/catch';
import { RepoType } from '@/utils/types';
import { useRoute, useRouter } from 'vue-router';
import { useRepoId } from '@/utils/hooks/useRepoId';
import { getProjectPublishList } from '@/api/aiHub/index';
import { PUBLISH_ERROR_LOG_MAP } from '@/constant/aihub';
import { type SpaceInfo } from '@/api/space/types';
import { getRepoSpace } from '@/api/space';
import { isGstarRepo, saveLastTimeRepoId } from '@/utils';
import { AUDIT_STATUS } from '@/constant/repo';
import { useTDKData } from '@/utils/hooks/useTDK';
import { transformUrl } from '@/utils/tools';

export type RepoAuditStatusType = AUDIT_STATUS.PASS | AUDIT_STATUS.AUDITING | AUDIT_STATUS.REJECTED;
interface IAuditInfo {
  field: string;
  value: string;
  audit_status: RepoAuditStatusType;
}
const { setTDKData } = useTDKData();
export interface repoInfoType {
  id?: number;
  name?: string;
  path?: string;
  path_with_namespace?: string;
  created_at?: string;
  star_count?: number;
  starred?: boolean;
  default_branch?: string; // 默认分支
  creator?: {
    id: number;
    iam_id: string;
    username: string;
    nick_name: string;
    avatar_url: string;
  };
  description?: string;
  homePage?: string;
  module_setting?: {
    repo_id: string;
    modules: {
      key: string;
      type: string;
      value: string;
    }[];
    homePage?: string;
  };
  web_url?: string;
  repo_type?: `${RepoType}`;
  /* 是否由安全漏洞派生 */
  forked_by_secure_issue: Record<string, any> | null;
  enterprise?: any;
  audit_fields?: IAuditInfo[];
  enable_issue_extend?: boolean;
  show_issue_extend?: boolean;
  [propName: string]: any;
}

const { visitor, developer, admin, maintainer, custom } = setting.role;

export const repoInfoStore = defineStore('globalRepoInfo', () => {
  const route = useRoute();
  const router = useRouter();
  const repoInfo = ref<repoInfoType>({});
  const access_level = ref<number>(0);
  const repoPageSkeleton = ref<boolean>(false); // 项目头部加载骨架状态
  const authNamespace = ref<boolean>(false); // 是否为白名单（UGR白名单）
  const alreadyGetAllWl = ref<boolean>(false); // 是否已经获取过白名单
  const AllwlCount = ref<number>(0); // 白名单调用失败次数
  const reinforce = ref<boolean>(false); // 是否为下载增强白名单
  const cangjie_experience = ref<boolean>(false); // 是否为仓吉体验白名单
  const gh_mirror_commit_show = ref<boolean>(false); // 是否为鏡像项目白名单
  const bindSpaceId = ref<number | null>(null); // 绑定的空间id
  const bindSpaceInfo = ref<SpaceInfo | undefined>(); // 绑定的空间信息
  const showFullTabs = ref<boolean>(true); // RepoHeaderInfo 组件是否显示完整tabs
  const refType = ref<'branch' | 'tag'>('branch'); // 当前项目分支类型
  const setRefType = (val: 'branch' | 'tag') => {
    refType.value = val;
  };
  const setShowFullTabs = (val: boolean) => {
    showFullTabs.value = val;
  };
  const isCodeHome = ref<boolean>(false); // 是否在代码首页
  const setIsCodeHome = (val: boolean) => {
    isCodeHome.value = val;
  };
  // 项目首页当前的分支
  const homeBranch = computed<string>(() => {
    // 优先使用 query 中的 ref 参数
    const queryRef = route?.query?.ref as string;
    if (queryRef) return queryRef;

    // 其次使用 params 中的 branchName
    const branchName = route?.params?.branchName;
    if (Array.isArray(branchName)) {
      return branchName.join('/');
    }
    return (branchName as string) || '';
  });

  /**
   * 自己平台star数（减去了github star）
   * @since 2024-11-29 后端已处理真实star数
   */
  const starCount = computed<number>(() => {
    return repoInfo.value.star_count || 0;
  });

  /**
   * 自己平台fork数（减去了github fork）
   * @since 2024-11-29 后端已处理真实fork数
   */
  const forkCount = computed<number>(() => {
    return repoInfo.value.forks_count || 0;
  });

  /* 该项目是否归属于某个企业的组织 */
  const isCorporateProject = computed<boolean>(() => {
    return !!repoInfo.value.enterprise?.id;
  });

  const setAccessLevel = (val: number) => {
    access_level.value = val;
  };

  const setRepoPageSkeleton = (val: boolean) => {
    repoPageSkeleton.value = val;
  };

  const setVisibility = (val: string) => {
    repoInfo.value = {
      ...repoInfo.value,
      visibility: val
    };
  };

  /** 是否为组织项目 */
  const isOrgProject = computed(() => {
    return repoInfo.value.namespace?.kind === 'group';
  });
  /** 是否为用户项目 */
  const isUserProject = computed(() => {
    return repoInfo.value.namespace?.kind === 'user';
  });

  const isPrivate = computed(() => {
    // 是否私有
    return repoInfo.value?.visibility === 'private';
  });

  const isArchived = computed(() => {
    // 是否归档
    return repoInfo.value?.archived;
  });

  /** 是否是模型项目 */
  const isModel = computed(() => {
    // 0: 代码仓 1: 模型 2: 数据集
    return repoInfo.value.repo_type === RepoType.model;
  });
  /** 是否是数据集 */
  const isDataSet = computed(() => {
    return repoInfo.value.repo_type === RepoType.dataset;
  });
  /** 是否是代码仓 */
  const isCode = computed(() => {
    return !repoInfo.value.repo_type || repoInfo.value.repo_type === RepoType.repo;
  });
  // 是否为spaces
  const isSpaces = computed(() => {
    return repoInfo.value.repo_type === RepoType.spaces;
  });

  const isAiHub = computed(() => {
    return isDataSet.value || isSpaces.value || isModel.value;
  });
  const isAdminOperate = computed(() => {
    // 操作权限（管理者且未归档，为了兼容未考虑归档时的权限）
    return !repoInfo.value?.archived;
  });

  const isDeveloperOperate = computed(() => {
    // 操作权限（管理者且未归档，为了兼容未考虑归档时的权限）
    return !repoInfo.value?.archived;
  });

  const isVisitorOperate = computed(() => {
    // 操作权限（管理者且未归档，为了兼容未考虑归档时的权限）
    return !repoInfo.value?.archived;
  });

  const isGhMirrors = computed(() => {
    const gh_mirrors = ['gh_mirrors', 'hf_mirrors', 'mirrors', 'GitHub_Trending']; // hf_mirrors和mirrors仓库审核不过来 暂时也作为gh_mirrors处理
    const pathStrList = repoInfo.value.path_with_namespace?.split('/') || [];
    const orgName = pathStrList[0]?.trim() || '';
    return gh_mirrors.includes(orgName);
  });

  // 工具类项目
  const isToolkit = computed(() => {
    const orgs = [
      'open-source-toolkit',
      'Resource-Bundle-Collection',
      'Open-source-documentation-tutorial',
      'Universal-Tool',
      'Premium-Resources'
    ];
    return orgs.includes(repoInfo.value?.namespace?.path);
  });

  // commit显示限制
  const LimitCommit = computed(() => {
    if (gh_mirror_commit_show.value || gh_mirror_commit_show.value === null) {
      return false;
    }
    return isGhMirrors.value;
  });

  // UGRLimit常驻限制 不使用Limit.value里面的控制
  // 排除镜像项目，G-star项目，用户创建项目，仅为使用import_url方式导入项目
  // 仅限制非项目成员
  const UGRLimit = computed(() => {
    const flag = access_level?.value <= 0;
    if (authNamespace.value) {
      // 如果处于白名单则不限制
      return false;
    }
    return !isGhMirrors.value && !isGstarRepo(repoInfo.value?.topic_names || []) && repoInfo.value?.import_url && flag;
  });

  // getAllWl
  const forkLimit = computed(() => {
    return !!repoInfo.value.forked_from_project && access_level?.value <= 0;
  });
  const isAdmin = computed(() => {
    // return access_level?.value >= admin;
    return true;
  });

  const isDeveloper = computed(() => {
    // return access_level?.value >= developer;
    return true;
  });

  const isVisitor = computed(() => {
    // return access_level?.value >= visitor;
    return true;
  });

  const isAdminType = computed(() => {
    return access_level?.value >= admin;
  });
  const isDeveloperType = computed(() => {
    return access_level?.value >= developer;
  });
  const isVisitorType = computed(() => {
    return access_level?.value >= visitor;
  });

  const isCustomType = computed(() => {
    // 后续会改
    return access_level?.value >= custom && access_level?.value !== 20;
  });
  const isDeveloperDo = computed(() => {
    // 操作权限（开发者且未归档，为了兼容未考虑归档时的权限）
    return access_level?.value >= developer && !repoInfo.value?.archived;
  });
  const isVisitorDo = computed(() => {
    // 操作权限（浏览者且未归档，为了兼容未考虑归档时的权限）
    return access_level?.value >= visitor && !repoInfo.value?.archived;
  });
  const isMaintainer = computed(() => {
    return access_level?.value >= maintainer;
  });
  /** 是维护者且未归档 */
  const isMaintainerDo = computed(() => {
    return access_level?.value >= maintainer && !repoInfo.value?.archived;
  });
  const isAdminDo = computed(() => {
    // 操作权限（管理者且未归档，为了兼容未考虑归档时的权限）
    return access_level?.value >= admin && !repoInfo.value?.archived;
  });
  const setRepoInfo = (data: repoInfoType) => {
    repoInfo.value = {
      ...repoInfo.value,
      ...data,
      namespace: {
        ...repoInfo.value.namespace,
        ...data.namespace
      }
    };
  };
  // 清除fork项目的fork来源仓库数据
  const clearForkedFromProject = () => {
    repoInfo.value.forked_from_project = null;
  };
  const updateRepoInfo = (data: repoInfoType) => {
    repoInfo.value = {
      ...repoInfo.value,
      ...data,
      projectFinished: true
    };
  };
  const updateRepoUpdateTime = () => {
    repoInfo.value = {
      ...repoInfo.value,
      repoUpdateTime: true
    };
  };
  const setBranchCount = (count: number) => {
    repoInfo.value.branch_count = count;
  };
  const setReleaseCount = (count: number) => {
    repoInfo.value.release_count = count;
  };
  const setTagsCount = (count: number) => {
    repoInfo.value.tag_count = count;
  };
  const setForkVisible = (visible: boolean) => {
    repoInfo.value.module_setting?.modules.forEach((item) => {
      if (item.key === 'FORK' && item.type === 'PROJECT_MODULE') {
        item.value = visible ? '1' : '0';
      }
    });
  };
  const getRepoInfo = async (namePath?: string) => {
    const res = await getRepo({ repoId: namePath || getRepoId(), statistics: true });
    if (!res.error) {
      const { data } = res.data;
      data.web_url = transformUrl(data.web_url);
      data.readme_url = transformUrl(data.readme_url);
      data.ssh_url_to_repo = transformUrl(data.ssh_url_to_repo);
      data.http_url_to_repo = transformUrl(data.http_url_to_repo);
      data.namespace.avatar_url = transformUrl(data.namespace.avatar_url);
      setTDKData({
        projectName: data.name,
        orgName: data.namespace?.path || ''
      });
      setBranchCount(data.branch_count || 0);
      return data;
    }
    return null;
  };

  const getRepoInfoWithAudit = async (namePath?: string) => {
    const res = await getRepoWithAudit({ repoId: namePath || getRepoId(), statistics: true });
    if (!res.error) {
      const { data } = res.data;
      data.web_url = transformUrl(data.web_url);
      data.readme_url = transformUrl(data.readme_url);
      data.ssh_url_to_repo = transformUrl(data.ssh_url_to_repo);
      data.http_url_to_repo = transformUrl(data.http_url_to_repo);
      data.namespace.avatar_url = transformUrl(data.namespace.avatar_url);
      setRepoInfo(data);
      return data;
    }
    return null;
  };

  const hasRoleAuth = (role: string, level?: number) => {
    if (level) return setting.role[role] <= level;
    else return setting.role[role] <= access_level.value;
  };
  // 更新项目id
  const updateSourceInfo = async () => {
    if (repoInfo.value.id) return; // 若已存在id，则不调用接口
    const res = await reqCatch(getSourceInfo, { path: getRepoId() });
    if (!res.error) {
      const { data } = res.data;
      repoInfo.value.id = Number(data.id);
    }
  };
  const publistInfo = ref({
    status: false,
    logs: []
  });
  const getCurPublistStatus = () => {
    return new Promise((resolve) => {
      getProjectPublishList({
        project_ids: [repoInfo.value.id],
        is_query_logs: true
      }).then((res) => {
        if (Array.isArray(res.data)) {
          const status = res.data[0]?.status;
          publistInfo.value.status = status;
          if (Array.isArray(res.data[0]?.audit_logs)) {
            const mapData = new Map();
            publistInfo.value.logs = res.data[0].audit_logs
              .filter((ele) => ele.success !== 1 && !mapData.has(ele.audit_code) && mapData.set(ele.audit_code, 1))
              .map((item) => PUBLISH_ERROR_LOG_MAP[item.audit_code] || '');
          }
        }
        resolve();
      });
    });
  };

  const getRepoRelateSpaceId = async () => {
    const res = await getRepoSpace({ project_id: repoInfo.value.id, pre_page: 1 });
    if (!res?.error) {
      bindSpaceId.value = res.data?.data?.content[0]?.space_project_id;
    } else {
      bindSpaceId.value = null;
    }
    return bindSpaceId.value;
  };
  watchEffect(() => {
    // 进入项目相关页面时保存最后一次的项目（有id就保存）
    if (repoInfo.value?.id) {
      saveLastTimeRepoId(repoInfo.value.id);
    }
    const { repoId } = useRepoId();
    if (repoId.value && !alreadyGetAllWl.value && AllwlCount.value <= 3) {
      alreadyGetAllWl.value = true;
      // 获取项目白名单
      getAllWl({ repoId: repoId.value, auth_namespace: true }).then((res) => {
        if (!res.error) {
          const { data } = res.data;
          authNamespace.value = data.auth_namespace;
          reinforce.value = data.reinforce;
          cangjie_experience.value = data.cangjie_experience;
          gh_mirror_commit_show.value = data.gh_mirror_commit_show;
          AllwlCount.value = 0;
        } else {
          alreadyGetAllWl.value = false;
          AllwlCount.value += 1;
        }
      });
    }
  });
  watch(
    () => repoInfo.value?.id,
    () => {
      if (isDataSet.value || isModel.value) {
        getCurPublistStatus();
      }
    }
  );

  // 判断是否为chat镜像项目
  const _isChatGhMirrors = () => {
    const gh_mirrors = ['gh_mirrors'];
    const pathStrList = repoInfo.value.path_with_namespace?.split('/') || [];
    const orgName = pathStrList[0]?.trim() || '';
    return gh_mirrors.includes(orgName);
  };

  // 判断 GitHub Star > 100
  const _gitHubStarLimit100 = () => {
    return (repoInfo?.value?.import_star_count || 0) > 100;
  };

  // 当前 route hash 跳转的issue 评论 id
  const issueJumpCommentId = ref<number>(0);

  const setIssueJumpCommentId = (data: number) => {
    issueJumpCommentId.value = data;
  };

  return {
    isPrivate,
    isAdmin,
    isMaintainer,
    isMaintainerDo,
    isDeveloper,
    isVisitor,
    isVisitorType,
    isDeveloperType,
    isAdminType,
    isArchived,
    isAdminOperate,
    isDeveloperOperate,
    isVisitorOperate,
    isAdminDo,
    isDeveloperDo,
    isVisitorDo,
    isCorporateProject,
    access_level,
    isCustomType,
    repoInfo,
    starCount,
    forkCount,
    repoPageSkeleton,
    isModel,
    isCode,
    isDataSet,
    isSpaces,
    isAiHub,
    isGhMirrors,
    isToolkit,
    LimitCommit,
    UGRLimit,
    forkLimit,
    homeBranch,
    reinforce,
    cangjie_experience,
    publistInfo,
    bindSpaceId,
    bindSpaceInfo,
    issueJumpCommentId,
    setIssueJumpCommentId,
    hasRoleAuth,
    setRepoInfo,
    updateRepoUpdateTime,
    updateRepoInfo,
    setVisibility,
    setAccessLevel,
    setReleaseCount,
    setTagsCount,
    setForkVisible,
    getRepoInfo,
    getRepoInfoWithAudit,
    setRepoPageSkeleton,
    updateSourceInfo,
    getCurPublistStatus,
    getRepoRelateSpaceId,
    showFullTabs,
    setShowFullTabs,
    isCodeHome,
    setIsCodeHome,
    clearForkedFromProject,
    isOrgProject,
    isUserProject,
    refType,
    setRefType
  };
});
